import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, Box, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFetchHealthcareServiceProviders } from 'hooks';
import { UserAvatar } from 'components/elements/UserAvatar';
import BookAppointmentService from './BookAppointmentService';
import ProgressSubtitle from './ProgressSubtitle';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function BookAppointmentProviders({ onSelectProvider, healthcareServiceId }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { data: brandingData } = useSelector((state) => state.branding);
  const { data: bookingProviders, isLoading } = useFetchHealthcareServiceProviders(healthcareServiceId);

  return (
    <>
      <ProgressSubtitle title={t('follow_up_appointment')} />
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && bookingProviders.length
        ? bookingProviders.map(({ providerName, id, tags, providerInitials, showCalendarAvailability, degrees, designations }) => (
            <BookAppointmentService key={id} onClick={() => onSelectProvider({ id, name: providerName, showCalendarAvailability })}>
              <div className={classes.provider}>
                <UserAvatar initials={providerInitials} />
                <div className={classes.providerInfoWrapper}>
                  <Typography className={classes.capitalizedTitle} variant="h6" component="h6" noWrap>
                    {providerName}
                  </Typography>
                  {degrees.map((degree) => (
                    <Typography className={classes.providerTag} variant="body2" component="span" key={degree.description}>
                      {degree.description}
                    </Typography>
                  ))}
                  {designations.map((designation) => (
                    <Typography className={classes.providerTag} variant="body2" component="span" key={designation.description}>
                      {designation.description}
                    </Typography>
                  ))}
                  {tags.map(
                    (tag) =>
                      tag?.length > 0 && (
                        <Typography className={classes.providerTag} variant="body2" component="span" key={tag}>
                          {tag}
                        </Typography>
                      ),
                  )}
                </div>
              </div>
            </BookAppointmentService>
          ))
        : null}
      {!isLoading && bookingProviders.length === 0 && (
        <Typography className={classes.noServiceTitle} variant="body1" component="div">
          {t('no_appointment_times_available_please_contact_brand_for_more_details', { brand: brandingData.displayName })}
        </Typography>
      )}
    </>
  );
}

BookAppointmentProviders.propTypes = {
  onSelectProvider: PropTypes.func.isRequired,
  healthcareServiceId: PropTypes.string,
};

BookAppointmentProviders.defaultProps = {
  healthcareServiceId: '',
};

export default BookAppointmentProviders;
